import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DataGlueGraph } from '@glueapp/graph-types';
import type { ExecutionResult, ExecutionError } from './useGraphExecution';
import type { GraphRunDetail } from './useGraphRuns';
import { checkRunCompatibility, filterToCompatibleResult, type RunCompatibility } from './helpers/runCompatibility';

const LIVE_PREVIEW_KEY = 'GLUE_LIVE_PREVIEW';

interface UseRunViewingReturn {
    activeRunId: string | null;
    activeRunDetail: GraphRunDetail | null;
    isLoadingRunDetail: boolean;
    viewRun: (runId: string) => void;
    clearRunView: () => void;
    viewedExecutionResult: ExecutionResult | undefined;
    viewedExecutionError: ExecutionError | undefined;
    isViewingRun: boolean;
    compatibility: RunCompatibility | null;
    livePreviewEnabled: boolean;
    toggleLivePreview: () => void;
}

export const useRunViewing = (
    fetchRunDetail: (runId: string) => Promise<GraphRunDetail>,
    graph: DataGlueGraph | undefined,
): UseRunViewingReturn => {
    const [activeRunId, setActiveRunId] = useState<string | null>(null);
    const [activeRunDetail, setActiveRunDetail] = useState<GraphRunDetail | null>(null);
    const [isLoadingRunDetail, setIsLoadingRunDetail] = useState(false);
    const [livePreviewEnabled, setLivePreviewEnabled] = useState<boolean>(() => {
        const stored = localStorage.getItem(LIVE_PREVIEW_KEY);
        return stored === null ? true : stored === 'true';
    });

    const toggleLivePreview = useCallback(() => {
        setLivePreviewEnabled(prev => {
            const next = !prev;
            localStorage.setItem(LIVE_PREVIEW_KEY, String(next));
            return next;
        });
    }, []);

    const viewRun = useCallback(
        (runId: string) => {
            setActiveRunId(runId);
            setActiveRunDetail(null);
            setIsLoadingRunDetail(true);
            fetchRunDetail(runId)
                .then(detail => {
                    setActiveRunDetail(detail);
                })
                .catch(err => {
                    console.error('Failed to fetch run detail:', err);
                    setActiveRunId(null);
                })
                .finally(() => {
                    setIsLoadingRunDetail(false);
                });
        },
        [fetchRunDetail],
    );

    const clearRunView = useCallback(() => {
        setActiveRunId(null);
        setActiveRunDetail(null);
        setIsLoadingRunDetail(false);
    }, []);

    const compatibility = useMemo(() => {
        if (!activeRunDetail?.result || !graph) return null;
        return checkRunCompatibility(graph, activeRunDetail.result as ExecutionResult);
    }, [activeRunDetail, graph]);

    const viewedExecutionResult = useMemo(() => {
        if (!activeRunDetail?.result) return undefined;
        const result = activeRunDetail.result as ExecutionResult;
        if (compatibility) {
            return filterToCompatibleResult(result, compatibility);
        }
        return result;
    }, [activeRunDetail, compatibility]);

    const viewedExecutionError = useMemo((): ExecutionError | undefined => {
        if (!activeRunDetail?.error) return undefined;
        return { message: activeRunDetail.error };
    }, [activeRunDetail]);

    const isViewingRun = activeRunId !== null;

    return {
        activeRunId,
        activeRunDetail,
        isLoadingRunDetail,
        viewRun,
        clearRunView,
        viewedExecutionResult,
        viewedExecutionError,
        isViewingRun,
        compatibility,
        livePreviewEnabled,
        toggleLivePreview,
    };
};
